/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { clone } from 'underscore';
import classnames from 'classnames';
import Backbone from 'backbone';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import BudgetView from '@bingads-webui-campaign-legacy/budget/view/budget';
import SharedBudgetView from '@bingads-webui-campaign-legacy/shared-budget/view/shared-budget-switch-editor';
import '@bingads-webui-campaign/ko-stop-binding';
import PageContext from '@bingads-webui-static-legacy/page-context';
import { mergeStyles } from '@bingads-webui-campaign-react/common-styles';

const getStyles = (theme, palette = {}) => ({
  budgetContainer: {
    ...mergeStyles(
      { theme },
      {
        '& .container-budget': {
          '& span': {
            color: palette.neutralPrimary,
          },
          '& .form-control-static': {
            padding: '9px 0px',
            lineHeight: '14px',
            minHeight: 'auto',
          },
          '& .input-group': {
            '& .input-group-addon': {
              color: '#323130',
              border: 0,
              padding: '6px',
              background: '#ffffff',
            },
          },
          '& .col-sm-3': {
            display: 'table',
            paddingRight: 0,
            paddingLeft: 0,
          },
          '& .margin-rt-five': {
            paddingLeft: 28,
          },

        },
        '& span.bold': {
          fontWeight: 1000,
        },
        '& .alert-budget-button': {
          marginLeft: '10px',
          paddingTop: '5px',
          '& button': {
            color: 'black',
            fontSize: '13px',
            fontWeight: 500,
            borderColor: '#edebe9',
          },
        },
        '& .shared-budget-switch-editor-grid': {
          '& .cell-selection': {
            '& input': {
              outline: 'none !important',
            },
          },
        },
      }
    ),
  },
});

export const LegacySharedBudget = React.memo(({
  id,
  className,
  campaignId,
  budget,
  onChange,
  i18n,
  validationDailyMinBudget,
  hideBudgetMinRecommendation,
  noApplyBudgetFromSharedLibrary,
  readOnly,
}) => {
  const view = useRef();
  const viewListener = useMemo(() => clone(Backbone.Events), []);
  let container;

  useEffect(() => {
    const budgetFactory = SharedBudgetView.newFactory(BudgetView);
    const showBudgetMinRecommend = campaignId !== undefined && !hideBudgetMinRecommendation;
    const minimumBudget = budget ? budget.Amount * 0.5 : undefined;

    view.current = budgetFactory({
      withRealTimeUpdates: true,
      model: budget,
      campaignId,
      isForVNext: true,
      hideDailyBudgetOptions: false,
      showBudgetMinRecommend,
      currencySymbol: i18n.getCurrencySymbol(PageContext.AccountCurrencyCode),
      minimumBudget,
      validationDailyMinBudget,
      showInitialUiBlocker: false,
      noApplyBudgetFromSharedLibrary,
      showNoSharedBudgetLink: true,
      readOnly,
    });

    viewListener.listenTo(view.current, 'change', onChange);
    view.current.render();
    view.current.$el.appendTo(container);

    return () => {
      view.current.remove();
    };
  }, [campaignId]);

  useEffect(() => {
    viewListener.stopListening(view.current);
    viewListener.listenTo(view.current, 'change', onChange);

    return () => viewListener.stopListening(view.current);
  }, [onChange, viewListener]);

  const classes = useDefaultStyles(getStyles);

  return (
    <div
      id={id}
      className={classnames(className, classes.budgetContainer)}
    >
      <div ref={(ref) => { container = ref; }} />
    </div>
  );
});

LegacySharedBudget.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  campaignId: PropTypes.string,
  budget: PropTypes.shape({
    Amount: PropTypes.number,
  }),
  onChange: PropTypes.func.isRequired,
  i18n: PropTypes.shape({ getCurrencySymbol: PropTypes.func.isRequired }).isRequired,
  validationDailyMinBudget: PropTypes.number,
  hideBudgetMinRecommendation: PropTypes.bool,
  noApplyBudgetFromSharedLibrary: PropTypes.bool,
  readOnly: PropTypes.bool,
};

LegacySharedBudget.defaultProps = {
  id: undefined,
  className: undefined,
  budget: undefined,
  campaignId: undefined,
  validationDailyMinBudget: undefined,
  hideBudgetMinRecommendation: true,
  noApplyBudgetFromSharedLibrary: false,
  readOnly: false,
};
